import { cn } from "@/lib/utils";

type Size = "xs" | "sm" | "md" | "lg" | "xl";

const SIZES: Record<Size, string> = {
  xs: "w-6 h-6 text-[10px]",
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-14 h-14 text-base",
  xl: "w-24 h-24 text-3xl"
};

const PALETTE = [
  "from-amber-500/20 to-amber-700/10 text-amber-200 border-amber-700/30",
  "from-rose-500/20 to-rose-700/10 text-rose-200 border-rose-700/30",
  "from-emerald-500/20 to-emerald-700/10 text-emerald-200 border-emerald-700/30",
  "from-sky-500/20 to-sky-700/10 text-sky-200 border-sky-700/30",
  "from-violet-500/20 to-violet-700/10 text-violet-200 border-violet-700/30",
  "from-fuchsia-500/20 to-fuchsia-700/10 text-fuchsia-200 border-fuchsia-700/30"
];

/**
 * Profile picture. Renders avatar_url if present, otherwise an
 * initial inside a coloured circle deterministically derived from
 * the seed (so the same user always gets the same colour).
 */
export function PfpAvatar({
  url,
  name,
  seed,
  size = "md",
  className,
  ring = false
}: {
  url?: string | null;
  name?: string | null;
  seed?: string;
  size?: Size;
  className?: string;
  ring?: boolean;
}) {
  const initial = ((name || seed || "?").trim()[0] || "?").toUpperCase();
  const idx = hash(seed || name || "?") % PALETTE.length;
  const palette = PALETTE[idx];

  const base = cn(
    "inline-flex shrink-0 items-center justify-center rounded-full border font-bold uppercase",
    SIZES[size],
    ring && "ring-2 ring-accent ring-offset-2 ring-offset-bg",
    className
  );

  if (url) {
    return <img src={url} alt={name || ""} className={cn(base, "object-cover border-line")} />;
  }
  return (
    <span className={cn(base, "bg-gradient-to-br", palette)}>
      {initial}
    </span>
  );
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
